import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { ArrowRight, FileText, Download, CheckCircle } from 'lucide-react';

const DownloadableResources = () => {
  const resources = [
    {
      title: "Export Compliance Checklist",
      description: "Comprehensive checklist covering ITAR, EAR, and customs compliance requirements for exporters.",
      pages: "8 pages",
      format: "PDF",
      category: "Compliance",
      topics: ["Export licensing", "Screening procedures", "Documentation requirements", "Recordkeeping"]
    },
    {
      title: "ECCN Quick Reference Guide",
      description: "Fast reference for understanding Export Control Classification Numbers and the Commerce Control List structure.",
      pages: "6 pages",
      format: "PDF",
      category: "Classification",
      topics: ["CCL categories", "ECCN format", "Reason for control codes", "Classification process"]
    },
    {
      title: "Red Flags Identification Worksheet",
      description: "Practical guide to identifying and responding to export control red flags in transactions.",
      pages: "4 pages",
      format: "PDF",
      category: "Compliance",
      topics: ["Warning signs", "Due diligence steps", "Documentation", "Reporting procedures"]
    },
    {
      title: "FTZ Cost-Benefit Calculator",
      description: "Excel template for calculating potential savings from Foreign Trade Zone operations.",
      pages: "1 file",
      format: "XLSX",
      category: "FTZ",
      topics: ["Duty savings calculation", "Weekly entry benefits", "Inverted tariff analysis", "ROI estimation"]
    },
    {
      title: "Voluntary Disclosure Decision Tree",
      description: "Step-by-step guide to determining when and how to submit voluntary self-disclosures.",
      pages: "5 pages",
      format: "PDF",
      category: "Compliance",
      topics: ["Violation assessment", "Disclosure requirements", "Mitigation strategies", "Timeline guidance"]
    },
    {
      title: "Product Classification Worksheet",
      description: "Structured template for documenting ECCN and HTS classifications with supporting analysis.",
      pages: "3 pages",
      format: "PDF",
      category: "Classification",
      topics: ["Product specifications", "Technical parameters", "Classification rationale", "Supporting documentation"]
    }
  ];

  return (
    <div className="resources-page">
      <SEO 
        title="Free Trade Compliance Resources & Templates Download"
        description="Download free export compliance checklists, ECCN guides, FTZ calculators, and trade compliance templates. Professional resources for import/export operations."
        keywords="export compliance checklist, ECCN guide, trade compliance templates, FTZ calculator, voluntary disclosure guide"
        path="/downloadable-resources"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'Downloadable Resources', path: '/downloadable-resources' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Free Trade Compliance Resources
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Download practical templates, checklists, and guides to support your trade compliance operations. All resources are professionally developed and ready to use.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {resources.map((resource, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <FileText className="h-10 w-10 text-slate-600 flex-shrink-0" />
                  <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                    {resource.category}
                  </span>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{resource.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{resource.description}</p>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    {resource.pages}
                  </span>
                  <span className="flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    {resource.format}
                  </span>
                </div>

                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Includes:</p>
                  <ul className="space-y-1">
                    {resource.topics.map((topic, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to="/contact">
                  <Button className="w-full bg-slate-700 hover:bg-slate-800 text-white">
                    Request Download
                    <Download className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-amber-50 border-2 border-amber-200 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Need Custom Templates?
            </h3>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              We can develop customized compliance templates, procedures, and documentation specific to your products, markets, and regulatory requirements.
            </p>
            <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Discuss Custom Solutions
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DownloadableResources;